import React from "react";
import ExtensionIcon from "@mui/icons-material/Extension";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { Box, Typography } from "@mui/material";
import AppleBottomNav from "./AppleBottomNav";

const systemFont = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif';

const LIGHT = {
    // Android HomeScreen / AppShell light theme (nav colours come from AppleBottomNav).
    pageBg: "#F9FAFB",
    title: "#1E293B",
    muted: "rgba(15, 23, 42, 0.65)",
    cardBg: "#FFFFFF",
    cardShadow: "0 10px 25px rgba(0, 0, 0, 0.04), inset 0 0 0 1px rgba(226, 232, 240, 0.8)",
    imageSlot: "rgba(15, 23, 42, 0.05)",
    navBg: undefined,
    navBorder: undefined,
    navActive: undefined,
    navInactive: undefined,
    resumeColor: "#0f1419",
    heroBg: "linear-gradient(135deg, #3b82f6 0%, #93c5fd 100%)",
    heroPiece: "rgba(255, 255, 255, 0.42)",
    playBg: "#E06A4E",
    playShadow: "none",
    top5: {
        background: "linear-gradient(180deg, #fff7ed 0%, #ffedd5 100%)",
        border: "1px solid rgba(234, 88, 12, 0.28)",
        shadow: "0 4px 12px rgba(234, 88, 12, 0.12)",
        titleColor: "#9a3412",
        subtitleColor: "#c2410c",
        actionColor: "#ea580c",
    },
    editors: {
        background: "linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)",
        border: "1.5px solid rgba(202, 138, 4, 0.55)",
        shadow: "0 4px 12px rgba(202, 138, 4, 0.14)",
        titleColor: "#854d0e",
        subtitleColor: "#a16207",
        actionColor: "#ca8a04",
    },
};

const DARK = {
    pageBg: "transparent",
    title: "#ecfdf5",
    muted: "rgba(236, 253, 245, 0.72)",
    cardBg: "rgba(2, 32, 24, 0.88)",
    cardShadow: "0 8px 32px rgba(0, 0, 0, 0.35)",
    imageSlot: "rgba(255, 255, 255, 0.08)",
    navBg: "rgba(2, 24, 18, 0.78)",
    navBorder: "rgba(110, 231, 183, 0.16)",
    navActive: "#6ee7b7",
    navInactive: "rgba(236, 253, 245, 0.72)",
    resumeColor: "rgba(236, 253, 245, 0.72)",
    heroBg: "linear-gradient(135deg, #0f766e 0%, #115e59 42%, #064e3b 100%)",
    heroPiece: "rgba(236, 253, 245, 0.4)",
    playBg: "linear-gradient(180deg, #4FC27A 0%, #3BA666 42%, #2F8F55 100%)",
    playShadow: "0 8px 22px rgba(59, 166, 102, 0.42), inset 0 1px 0 rgba(255,255,255,0.28)",
    top5: {
        background: "linear-gradient(180deg, rgba(154, 52, 18, 0.55) 0%, rgba(124, 45, 18, 0.7) 100%)",
        border: "1px solid rgba(251, 146, 60, 0.4)",
        shadow: "0 4px 12px rgba(0, 0, 0, 0.28)",
        titleColor: "#fed7aa",
        subtitleColor: "#fdba74",
        actionColor: "#fb923c",
    },
    editors: {
        background: "linear-gradient(180deg, rgba(133, 77, 14, 0.55) 0%, rgba(113, 63, 18, 0.7) 100%)",
        border: "1.5px solid rgba(250, 204, 21, 0.4)",
        shadow: "0 4px 12px rgba(0, 0, 0, 0.28)",
        titleColor: "#fde68a",
        subtitleColor: "#fcd34d",
        actionColor: "#facc15",
    },
};

const HERO_PIECES = [
    { outline: false, top: "-12px", left: "6%", size: 54, rotate: -16, opacity: 0.7 },
    { outline: true, top: "2px", right: "26%", size: 38, rotate: 24, opacity: 0.7 },
    { outline: false, top: "12px", right: "-20px", size: 92, rotate: 18, opacity: 0.7 },
    { outline: true, top: "20px", left: "-22px", size: 84, rotate: -22, opacity: 0.7 },
    { outline: false, top: "-2px", left: "47%", size: 34, rotate: 10, opacity: 0.7 },
];

function HeroPieces({ background, pieceColor }) {
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                background,
            }}
        >
            {HERO_PIECES.map((piece, index) => {
                const PieceIcon = piece.outline ? ExtensionOutlinedIcon : ExtensionIcon;
                return (
                    <Box
                        key={index}
                        sx={{
                            position: "absolute",
                            top: piece.top,
                            left: piece.left,
                            right: piece.right,
                            lineHeight: 0,
                            color: pieceColor,
                            opacity: piece.opacity,
                        }}
                    >
                        <PieceIcon
                            sx={{
                                display: "block",
                                fontSize: piece.size,
                                transform: `rotate(${piece.rotate}deg)`,
                            }}
                        />
                    </Box>
                );
            })}
        </Box>
    );
}

function QuickNavCard({ title, subtitle, action, accent }) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 0.25,
                minHeight: 44,
                minWidth: 0,
                width: "100%",
                px: 1.25,
                py: 1,
                borderRadius: "14px",
                border: accent.border,
                background: accent.background,
                boxShadow: accent.shadow,
            }}
        >
            <Typography
                component="span"
                sx={{
                    fontFamily: systemFont,
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: accent.titleColor,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                }}
            >
                {title}
            </Typography>
            <Typography
                component="span"
                sx={{
                    fontFamily: systemFont,
                    fontSize: "0.6875rem",
                    fontWeight: 500,
                    lineHeight: 1.25,
                    color: accent.subtitleColor,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                }}
            >
                {subtitle}
            </Typography>
            <Typography
                component="span"
                sx={{
                    fontFamily: systemFont,
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: accent.actionColor,
                    mt: 0.125,
                }}
            >
                {action}
            </Typography>
        </Box>
    );
}

/**
 * Jigsaw Muse home — resume, featured puzzle, PLAY, Top 5 / Editor's Picks.
 * Static App Store shot (matches the live home screen).
 */
export default function AppleHomePage({
    title,
    imageUrl,
    publishedLabel = "33 minutes ago",
    dark = false,
}) {
    const theme = dark ? DARK : LIGHT;
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                minHeight: "100%",
                bgcolor: theme.pageBg,
                overflow: "hidden",
                position: "relative",
            }}
        >
            {dark ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "calc(env(safe-area-inset-top, 0px) + 220px)",
                        overflow: "hidden",
                        pointerEvents: "none",
                        zIndex: 0,
                    }}
                >
                    <HeroPieces background={theme.heroBg} pieceColor={theme.heroPiece} />
                </Box>
            ) : null}

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flexShrink: 0,
                    px: 2,
                    pt: "max(env(safe-area-inset-top, 0px), 10px)",
                    minHeight: 48,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                        fontWeight: 800,
                        letterSpacing: 0,
                        fontSize: "1.25rem",
                        textTransform: dark ? "uppercase" : "none",
                        // Android AppShell `brandHome`: slate title on canvas (no hero band in the live app).
                        color: dark ? "#ffffff" : "#1E293B",
                    }}
                >
                    Jigsaw Muse
                </Typography>
            </Box>

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    px: 1.5,
                    pt: 0.75,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        mb: 1.25,
                        p: 1.25,
                        borderRadius: "20px",
                        background: theme.cardBg,
                        boxShadow: theme.cardShadow,
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minHeight: 44,
                            px: 0.5,
                            py: 0.75,
                            fontFamily: systemFont,
                            fontWeight: 600,
                            fontSize: "1rem",
                            color: theme.resumeColor,
                        }}
                    >
                        Last game
                    </Box>
                    <Typography
                        sx={{
                            fontSize: "0.8125rem",
                            lineHeight: 1.45,
                            color: theme.muted,
                            fontFamily: systemFont,
                            px: 0.5,
                        }}
                    >
                        No puzzles in progress. Play from Gallery.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        flex: "1 1 auto",
                        minHeight: 0,
                        borderRadius: "20px",
                        overflow: "hidden",
                        background: theme.cardBg,
                        boxShadow: theme.cardShadow,
                        display: "flex",
                        flexDirection: "column",
                        p: 1.25,
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            flex: "1 1 auto",
                            minHeight: 0,
                            borderRadius: "12px",
                            overflow: "hidden",
                            bgcolor: theme.imageSlot,
                        }}
                    >
                        {imageUrl ? (
                            <Box
                                component="img"
                                src={imageUrl}
                                alt={title}
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                }}
                            />
                        ) : null}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.375,
                            pt: 1.25,
                            pb: 0.5,
                            flexShrink: 0,
                        }}
                    >
                        {title ? (
                            <Typography
                                component="h2"
                                sx={{
                                    fontFamily: systemFont,
                                    fontSize: "1.15rem",
                                    fontWeight: 600,
                                    color: theme.title,
                                    lineHeight: 1.35,
                                    textAlign: "center",
                                }}
                            >
                                {title}
                            </Typography>
                        ) : null}
                        <Typography
                            sx={{
                                fontFamily: systemFont,
                                fontSize: "0.875rem",
                                color: theme.muted,
                                lineHeight: 1.3,
                            }}
                        >
                            {publishedLabel}
                        </Typography>
                        <Box
                            sx={{
                                mt: 1,
                                width: "100%",
                                maxWidth: 420,
                                minHeight: 56,
                                borderRadius: "999px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.35,
                                color: "#ffffff",
                                fontFamily: systemFont,
                                fontWeight: 800,
                                fontSize: "1.125rem",
                                letterSpacing: "0.06em",
                                background: theme.playBg,
                                boxShadow: theme.playShadow,
                            }}
                        >
                            PLAY
                            <PlayArrowRoundedIcon sx={{ fontSize: 28 }} />
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 1.5,
                        width: "100%",
                        flexShrink: 0,
                        mt: 1,
                        mb: 1,
                    }}
                >
                    <QuickNavCard
                        title="🔥 Top 5"
                        subtitle="Most Popular Played"
                        action="View All →"
                        accent={theme.top5}
                    />
                    <QuickNavCard
                        title="✨ Editor's Picks"
                        subtitle="Hand-picked Collections"
                        action="Explore →"
                        accent={theme.editors}
                    />
                </Box>
            </Box>

            <AppleBottomNav
                activeTab="home"
                dark={dark}
                navBg={theme.navBg}
                navBorder={theme.navBorder}
                activeColor={theme.navActive}
                inactiveColor={theme.navInactive}
            />
        </Box>
    );
}
